import { Link } from "react-router-dom";
import type { RunStatus, RunSummary } from "@agentdash/shared";
import { STUCK_RUN_THRESHOLD_MS } from "@agentdash/shared";
import { useLiveRunningRuns, useRuns } from "../../store/hooks.js";
import { StatusDot } from "../../components/StatusDot.js";
import { SummaryCell } from "../../components/SummaryCell.js";
import { Elapsed } from "../../components/Elapsed.js";
import { Timestamp } from "../../components/Timestamp.js";
import { elapsedMs, formatDurationMs } from "../../lib/time.js";
import { UpcomingRuns } from "./UpcomingRuns.js";

const FINISHED: readonly RunStatus[] = ["ok", "error"];

function LiveRow({ run }: { run: RunSummary }) {
  const stuck = elapsedMs(run.started_at) > STUCK_RUN_THRESHOLD_MS;
  return (
    <tr className={stuck ? "row-stuck" : undefined}>
      <td className="mc-status">
        <StatusDot status={run.status} />
      </td>
      <td className="mc-title">
        <Link to={`/runs/${run.trace_id}`}>{run.name}</Link>
      </td>
      <td data-label="Source">{run.source}</td>
      <td data-label="Repo">{run.repo ?? "—"}</td>
      <td data-label="Host">{run.host ?? "—"}</td>
      <td data-label="Started">
        <Timestamp iso={run.started_at} />
      </td>
      <td data-label="Elapsed">
        <Elapsed startedAt={run.started_at} />
      </td>
    </tr>
  );
}

function CompletedRow({ run }: { run: RunSummary }) {
  return (
    <tr>
      <td className="mc-status">
        <StatusDot status={run.status} />
      </td>
      <td className="mc-title">
        <Link to={`/runs/${run.trace_id}`}>{run.name}</Link>
      </td>
      <td data-label="Source">{run.source}</td>
      <td data-label="Repo">{run.repo ?? "—"}</td>
      <td data-label="Host">{run.host ?? "—"}</td>
      <td data-label="Started">
        <Timestamp iso={run.started_at} />
      </td>
      <td data-label="Duration">{formatDurationMs(run.duration_ms)}</td>
      <td data-label="Exit">{run.exit_code ?? "—"}</td>
      <SummaryCell run={run} />
    </tr>
  );
}

export function LiveView() {
  // Not useRuns({status: "running", ...}): this must be the exact key the
  // /api/live snapshot frame writes to, which useLiveRunningRuns guarantees.
  const runningQ = useLiveRunningRuns();
  // Filtered server-side: fetching the latest 20 of any status and dropping the
  // running ones here left the panel short — empty, with 20 runs in flight.
  const completedQ = useRuns({ status: FINISHED, limit: 20 });
  const completed = completedQ.data ?? [];

  return (
    <>
      <UpcomingRuns />

      <section className="panel">
        <div className="panel-header">Running now</div>
        {runningQ.isError ? (
          <div style={{ padding: 16, color: "var(--err)" }}>
            Failed to load running runs:{" "}
            {String((runningQ.error as Error).message ?? runningQ.error)}
          </div>
        ) : runningQ.isLoading ? (
          <div style={{ padding: 16 }}>Loading…</div>
        ) : (runningQ.data ?? []).length === 0 ? (
          <div style={{ padding: 16, color: "var(--muted)" }}>
            Nothing is running.
          </div>
        ) : (
          <table className="mobile-cards">
            <thead>
              <tr>
                <th />
                <th>Name</th>
                <th>Source</th>
                <th>Repo</th>
                <th>Host</th>
                <th>Started</th>
                <th>Elapsed</th>
              </tr>
            </thead>
            <tbody>
              {(runningQ.data ?? []).map((r) => (
                <LiveRow key={r.trace_id} run={r} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <div className="panel-header">Recently completed</div>
        {completedQ.isError ? (
          <div style={{ padding: 16, color: "var(--err)" }}>
            Failed to load completed runs:{" "}
            {String((completedQ.error as Error).message ?? completedQ.error)}
          </div>
        ) : completedQ.isLoading ? (
          <div style={{ padding: 16 }}>Loading…</div>
        ) : completed.length === 0 ? (
          <div style={{ padding: 16, color: "var(--muted)" }}>
            No completed runs yet.
          </div>
        ) : (
          <table className="mobile-cards">
            <thead>
              <tr>
                <th />
                <th>Name</th>
                <th>Source</th>
                <th>Repo</th>
                <th>Host</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Exit</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {completed.map((r) => (
                <CompletedRow key={r.trace_id} run={r} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
